import { useCombobox } from 'downshift';
import { useState } from 'react';
import { Icon } from '../Icon/icon';
import { ComboboxItem } from '../../commonTypes/multiSelect.types';
import { InputError } from '../InputError/InputError';
import { FormFieldProps, FormFieldString, FormField } from '../../commonTypes/formField.types';
import { hashCode } from '../../utils/hash';
import React from 'react';

export interface IComboboxWithSearchFormControlProps extends FormFieldProps<string> {
  items: ComboboxItem[];
  textBoxDefaultText?: string;
  label?: string;
  labelClass?: string;
  useHeadingInsteadOfLabel?: boolean;
  headingLevel?: number;
  className?: string;
  placeholder?: string;
}

export interface ComboboxWithSearchState extends FormFieldString {
  selectedText: string;
  clicked: boolean;
}

//Default props
ComboboxWithSearchFormControl.defaultProps = {
  textBoxDefaultText: 'Välj en',
  label: 'Välj en',
  labelClass: '',
  items: [],
  useHeadingInsteadOfLabel: false,
  headingLevel: 2,
  value: '',
  onChange: (value: FormField<any>): void => {},
} as Partial<IComboboxWithSearchFormControlProps>;

export function ComboboxWithSearchFormControl(props: IComboboxWithSearchFormControlProps) {
  const [inputItems, setInputItems] = useState(props.items.slice());

  const _errorId = hashCode(props.id || props.fieldName, 'error');

  const comboboxApi = useCombobox({
    items: inputItems.slice(),
    onInputValueChange: ({ inputValue, type }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        const dropdownItems = props.items.filter((item) =>
          item.value.toLowerCase().startsWith(inputValue.toLowerCase())
        );
        setInputItems(dropdownItems);
      } else {
        setInputItems(props.items);
      }
    },
    onIsOpenChange: ({ type }) => {
      setInputItems(props.items);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        const data = {
          value: selectedItem.key,
          isTouched: true,
          selectedText: selectedItem.value,
          fieldName: props.fieldName,
        } as ComboboxWithSearchState;
        props.onChange(data);
      }
    },
    itemToString: (item) => item.value,
    defaultSelectedItem: props.items.find((x) => x.key === props.value),
    getA11ySelectionMessage: ({ selectedItem, itemToString }) => {
      if (selectedItem) {
        return `${itemToString(selectedItem)} har valts.`;
      }
      return 'Välj en.';
    },
    getA11yStatusMessage: ({ inputValue, resultCount, isOpen, previousResultCount }) => {
      if (!isOpen) {
        return '';
      }

      if (resultCount === 0) {
        return `Inga resultat hittades.`;
      }

      if (resultCount !== previousResultCount) {
        return `${resultCount} resultat hittades. Använd pilknapparna för att navigera mellan dem. Tryck enter för att välja.`;
      }

      return '';
    },
  });

  // Destructure with fallback to avoid runtime "getComboboxProps is not a function" if multiple Downshift versions caused mismatch
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps: _getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = comboboxApi as any;

  const safeGetComboboxProps = typeof _getComboboxProps === 'function' ? _getComboboxProps : () => ({ className: '' });

  const HTag = `h${props.headingLevel}`;

  return (
    <div className="cmb-search">
      {props.useHeadingInsteadOfLabel && (
        <HTag {...getLabelProps()} className={`${props.labelClass} inline`}>
          {props.label}
        </HTag>
      )}
      {!props.useHeadingInsteadOfLabel && (
        <label {...getLabelProps()} className={props.labelClass}>
          {props.label}
        </label>
      )}
      <div className={props.className ?? ''} {...safeGetComboboxProps()}>
        <input {...getInputProps()} placeholder={props.placeholder ?? ''} name={props.fieldName ?? ''} />
        <button
          type="button"
          {...getToggleButtonProps()}
          aria-label="växla meny"
          className="cmb-search__btn"
          tabIndex="0">
          <Icon
            className={`cmb-search__arrow ${isOpen ? 'cmb-search__arrow--expanded' : ''}`}
            iconType="fontawesome/chevron-left"
          />
        </button>
      </div>
      <ul {...getMenuProps()} className="cmb-search__content">
        {isOpen && (
          <>
            {inputItems.map((item, index) => (
              <li
                className={highlightedIndex === index ? 'cmb-search__active' : ''}
                key={`${item.key}_${index}`}
                {...getItemProps({
                  item,
                  index,
                })}>
                {item.value}
              </li>
            ))}
          </>
        )}
      </ul>
      {props.isTouched && props.errorMessage && <InputError id={_errorId} errorText={props.errorMessage} />}
    </div>
  );
}
